import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil, mergeMap, tap } from 'rxjs/operators';

import {
  Customer,
  CustomerAddress,
  CustomerPhone,
} from 'src/app/shared/models/customer.model';
import {
  RecurringInvoice,
  RecurringInvoiceConfig,
  RecurringPayment,
  FrequencyRepeatTypes,
  FrequencyEndType,
  SettingsInvoice,
} from 'src/app/invoice/models/invoice.model';
import { User } from 'src/app/core/models/user.model';
import { Settings } from 'src/app/shared/models/settings/settings.model';
import { LocationModel } from 'src/app/settings/models/location/location-details.model';

import { AuthService } from 'src/app/core/auth.service';
import { InvoiceService } from 'src/app/invoice/services/invoice-service.service';
import { UserService } from 'src/app/shared/services/user.service';
import { RecurringPaymentService } from 'src/app/invoice/services/recurring-payment.service';
import { CustomerManagementService } from 'src/app/shared/services/customer.service';
import { ServiceWorkOrderService } from 'src/app/shared/services/service-work-order.service';
import { RecurringInvoiceService } from 'src/app/invoice/services/recurring-invoice.service';
import { AlertService } from 'src/app/core/alert.service';

import { PaymentOptionsComponent } from 'src/app/shared/components/payment-options_revamp/payment-options.component';
import { PauseRecurringInvoiceDialogComponent } from 'src/app/invoice/invoice-detail-view/pause-recurring-invoice-dialog/pause-subscription-dialog.component';
import { RecurringInvoiceSuspensionFormComponent } from 'src/app/invoice/invoice-detail-view/recurring-invoice-suspension-form/subscription-suspension-form.component';
import { RecurringInvoiceCancellationComponent } from 'src/app/invoice/customer-invoice-details/recurring-invoice-cancellation/subscription-cancellation.component';
import { getFrequencyText, mapFrequencyRule } from 'src/app/invoice/utilities';

interface Response {
  [key: string]: any;
}

@Component({
  selector: 'app-recurring-payment-detail',
  templateUrl: './recurring-payment-detail.component.html',
  styleUrls: ['./recurring-payment-detail.component.scss']
})
export class RecurringPaymentDetailComponent implements OnInit, OnDestroy {
  @ViewChild('recurringForm', { static: true }) recurringForm: NgForm;

  @Input() recurringId: number;
  @Input() invoiceNumber: string;
  @Input() status: string;

  isLoading: boolean = false;
  isSaving: boolean = false;
  isProcessing: boolean = false;
  statusChange: string;
  recurring: any;
  invoices: any[];
  customer: Customer;
  customerAddresses: CustomerAddress[];
  customerContacts: CustomerPhone[];
  customerBusinessContact: any;
  customerSignature: any;
  customerCards: any[] = [];
  selectedCard: any;
  recurringInvoiceConfig: RecurringInvoiceConfig;

  userInfo: User;
  invoice: any;
  recurringInvoice: any;
  workOrder: any;
  invoiceSettings: SettingsInvoice = new SettingsInvoice();
  businessInfo: Settings = new Settings();
  locationData: LocationModel;
  paymentConfig: any = {
    showCashPayment: true,
    hideManualPaymentOption: false,
    showACHPaymentOption: true,
    showCreditCardOption: true,
  };

  gridApi: any;
  gridColumnApi: any;
  gridColumns: any = [
    {
      headerName: 'Invoice Number',
      field: 'invoiceNumber',
      sortable: true,
      cellClass: 'tooltip-enabled',
      cellRenderer: this.gridInvoiceNumberRenderer.bind(this),
    },
    {
      headerName: 'Amount',
      field: 'amount',
      sortable: true,
      cellRenderer: (data) => {
        return data.value
          ? `<strong>${this.currencyPipe.transform(data.value, 'USD', '$ ')}</strong>`
          : '-';
      },
    },
    {
      headerName: 'Status',
      field: 'invoiceStatus',
      sortable: true,
      cellRenderer: this.gridStatusRenderer.bind(this),
    },
    {
      headerName: 'Retry',
      field: 'retryCount',
      width: 120,
      sortable: true,
    },
    {
      headerName: 'Payment Date',
      field: 'paymentDate',
      sortable: true,
      cellClass: 'tooltip-enabled',
      cellRenderer: (data) => {
        return data.value ? `${this.datePipe.transform(data.value, 'yyyy-MM-dd')} <span class="tooltips">${this.datePipe.transform(data.value, 'yyyy-MM-dd')}</span>` : '-';
      },
    },
    {
      headerName: 'Actions',
      width: 275,
      resizable: true,
      cellRenderer: this.gridActionRenderer.bind(this),
    },
  ];

  readonly unsubscribe$ = new Subject();

  constructor(
    private modalRef: NgbActiveModal,
    private modalService: NgbModal,
    private authService: AuthService,
    private userService: UserService,
    private invoiceService: InvoiceService,
    private recurringInvoiceService: RecurringInvoiceService,
    private customerService: CustomerManagementService,
    private workOrderService: ServiceWorkOrderService,
    private alertService: AlertService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.statusChange = this.status.toLowerCase();
    this.userInfo = JSON.parse(this.authService.getUserInfo());
    this.getRecurringDetails();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onClose(data?: any) {
    this.modalRef.close(data);
  }

  onGridReady(params): void {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    this.gridApi.sizeColumnsToFit();
    this.gridApi.setDomLayout('autoHeight');
  }

  gridStatusRenderer(params): string {
    const status = params.data.invoiceStatus;
    return `<span class="tag-pill tag-${status.toLowerCase()}">${status}</span>`;
  }

  gridInvoiceNumberRenderer(params): string {
    const invoiceNumber = params.data.invoiceNumber;
    return `${invoiceNumber} <span class="tooltips">${invoiceNumber}</span>`;
  }

  private gridActionRenderer(params): HTMLElement {
    const self = this;
    const eDiv = document.createElement('div');

    let template = `
      <div>
        <buton style="width: 100%;font-size: 0.75rem;" cjbutton class="!bg-blue-10 !text-blue-90 !border-blue-10 appearance-filled size-small icon-start status-primary process">
          <i class="fas fa-sync"></i> Process
        </button>
      </div>
    `;

    if (!['Paid', 'Voided', 'Refunded'].includes(params.data.invoiceStatus)) {
      const classes = ['!bg-blue-10', '!text-blue-90', '!border-blue-10'];
      eDiv.innerHTML = template;

      let processBtn = eDiv.querySelector('.process') as HTMLElement;
      processBtn.addEventListener('click', () => {
        this.onProcessPayment(params.data.invoiceNumber);
      });

      if (this.isProcessing) {
        processBtn.style.opacity = '0.5';
        processBtn.style.pointerEvents = 'none';
      } else {
        processBtn.style.opacity = '1';
        processBtn.style.pointerEvents = 'all';
      }
    }

    return eDiv;
  }

  getRecurringDetails(): void {
    this.isLoading = true;

    this.recurringInvoiceService
      .getRecurringPaymentFormDetails(this.recurringId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((recurring: any) => (this.recurring = recurring)),
        mergeMap(() => {
          let data: any = {
            invoices: this.recurringInvoiceService.getRecurringPaymentFormChildInvoices(this.recurringId),
            customer: this.customerService.getCustomer(this.recurring.customerId),
            customerAddresses: this.customerService.getCustomerAddresses(this.recurring.customerId),
            customerContacts: this.customerService.getCustomerContactPersons(this.recurring.customerId),
            customerCards:  this.customerService.getCustomerCardsOnFile(this.recurring.customerId),
          };

          return forkJoin({ ...data });
        })
      )
      .subscribe(
        (data: any) => {
          for (const [key, value] of Object.entries(data)) {
            this[key] = value;

            if (key === 'customerCards') {
              if (this.customerCards.length) {
                this.onSelectCard(this.recurring.customerCardNumberId);
              } else {
                this.selectedCard = null;
              }
            }
          }
          this.isLoading = false;
        },
        (error) => {
          console.log(error);
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
  }

  getStatus(isClass: boolean = false): string {
    let statusLabel: string = this.status;

    if (this.status === 'Active') {
      statusLabel = 'Recurring Active';
    }

    if (this.status === 'Inactive') {
      statusLabel = 'Cancelled';
    }

    if (isClass) {
      return this.status.replace(/\s+/g, '-').toLowerCase();
    }

    return statusLabel;
  }

  getDefaultCustomerAddress(): string {
    if (this.customerAddresses) {
      const address = this.customerAddresses.find((e) => e.IsDefault);

      if (address) {
        const fullAddress = `${address.StreetAddress || ''} ${address.City || ''} ${address.State || ''} ${address.ZipCode || ''}`;
        return fullAddress.replace(/\s/g, '') ? fullAddress : null;
      }
    }

    return;
  }

  getDefaultPhone(): string {
    if (this.customerContacts) {
      const phone = this.customerContacts.find((e) => e.IsDefault);

      if (phone) {
        return phone.PhoneNumber;
      }
    }

    return;
  }

  getFrequency(frequency: any): string {
    const rules = mapFrequencyRule(frequency);
    return getFrequencyText(rules);
  }

  canAccessMore(): boolean {
    return (
      (this.canCancel() ||
        this.canPause() ||
        this.canResume()
        // this.canSuspend()
    ));
  }

  canCancel(): boolean {
    return (
      ['Completed', 'Cancelled', 'Cancel Pending'].indexOf(
        this.status
      ) === -1
    );
  }

  canPause(): boolean {
    return (
      ['Completed', 'Paused', 'Cancelled'].indexOf(
        this.status
      ) === -1
    );
  }

  canResume(): boolean {
    return this.status === 'Paused';
  }

  canSuspend(): boolean {
    return (
      ['Suspended', 'Cancelled', 'Completed'].indexOf(
        this.status
      ) === -1
    );
  }

  onSelectCard(id: number): void {
    this.selectedCard = this.customerCards.find((e) => e.Id === id);
  }

  onSaveChanges(): void {
    this.isSaving = true;
    const recurringInvoice = new RecurringInvoice();
    recurringInvoice.Configuration = { ...this.recurringInvoiceConfig };
    recurringInvoice.Configuration.StartDate = this.datePipe.transform(this.recurring.startDate,'yyyy-MM-dd');
    recurringInvoice.Configuration.RecurringFrequency = { ...this.recurringInvoiceConfig.RecurringFrequency };
    recurringInvoice.Configuration.CustomerCardNumberId = this.selectedCard.Id;
    recurringInvoice.Configuration.RecurringInvoiceId = this.recurring.id;
    recurringInvoice.Id = this.recurring.id;
    recurringInvoice.ManualInvoiceId = this.recurring.manualInvoiceId;
    recurringInvoice.Status = this.status;

    const frequencyObject = recurringInvoice.Configuration.RecurringFrequency;
    switch (frequencyObject.FrequencyType) {
      case FrequencyRepeatTypes.Daily:
        delete frequencyObject['ByDay'];
        delete frequencyObject['ByMonth'];
        delete frequencyObject['ByMonthDay'];
        delete frequencyObject['BySetPos'];
        break;
      case FrequencyRepeatTypes.Weekly:
        delete frequencyObject['ByMonth'];
        delete frequencyObject['ByMonthDay'];
        delete frequencyObject['BySetPos'];
        break;
      case FrequencyRepeatTypes.Monthly:
        delete frequencyObject['ByMonth'];
        if (frequencyObject.DayType === 'day') {
          delete frequencyObject['ByDay'];
          delete frequencyObject['BySetPos'];
        } else {
          delete frequencyObject['ByMonthDay'];
        }
        break;
      case FrequencyRepeatTypes.Yearly:
        if (frequencyObject.DayType === 'day') {
          delete frequencyObject['ByDay'];
          delete frequencyObject['BySetPos'];
        } else {
          delete frequencyObject['ByMonthDay'];
        }
        break;
    }

    switch (+frequencyObject.EndType) {
      case FrequencyEndType.Never:
        delete frequencyObject['Count'];
        delete frequencyObject['Until'];
        break;
      case FrequencyEndType.Until:
        delete frequencyObject['Count'];
        break;
      case FrequencyEndType.Count:
        delete frequencyObject['Until'];
        break;
    }
    delete frequencyObject['ByDayArray'];
    delete frequencyObject['DayType'];
    delete frequencyObject['EndType'];

    this.recurringInvoiceService
      .saveRecurringInvoice(recurringInvoice)
      .subscribe({
        error: (error) => {
          this.isSaving = false;
          this.alertService.alertError({ title: 'Error', text: error.error });
        },
        complete: () => {
          this.isSaving = false;
          this.alertService.alertSuccess({
            title: 'Success',
            text: 'Recurring payment successfully saved!',
          });
          this.onClose();
        },
      });
  }

  onProcessPayment(invoiceNumber: string): void {
    this.isProcessing = true;
    this.gridApi.refreshCells();

    this.invoiceService
      .getManualInvoice(invoiceNumber)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((invoice: any) => (this.invoice = invoice)),
        mergeMap(() => {
          let summaryData: any = {
            businessInfo: this.userService.getMerchant('1'),
            invoiceSettings: this.invoiceService.getSettingsInvoice(),
            customerSignature:
              this.invoiceService.getCustomerSigByInvoiceNumber(
                invoiceNumber
              ),
            customerBusinessContact:
              this.customerService.getCustomerContactPersonById(
                this.invoice.businessCustomerContactPersonId
              ),
            workOrder: this.workOrderService.getWorkOrderByInvoice(
              invoiceNumber
            ),
            locationData: this.authService.getLocationById(
              this.invoice.locationId
            ),
          };

          return forkJoin({ ...summaryData });
        })
      )
      .subscribe({
        next: (data: Response) => {
          for (const [key, value] of Object.entries(data)) {
            if (key === 'locationData') {
              this.locationData = value.locationComplete;
              this.paymentConfig.showACHPaymentOption =
                this.locationData?.ACHEnabled;
              this.paymentConfig.showCreditCardOption =
                this.locationData?.CCEnabled;

              continue;
            }

            if (key === 'customerCards') {
              this.customerCards = value;

              if (this.customerCards.length) {
                this.onSelectCard(this.recurring.customerCardNumberId);
              } else {
                this.selectedCard = null;
              }
              continue;
            }
            this[key] = value;
          }
        },
        error: (error) => {
          console.log(error);
          this.isProcessing = false;
          this.gridApi.refreshCells();
        },
        complete: () => {
          let paymentDiag = this.modalService.open(PaymentOptionsComponent, {
            backdrop: 'static',
          });

          paymentDiag.componentInstance.isRecurringPayment = true;
          paymentDiag.componentInstance.recurringPaymentDetails = {
            recurringId: this.recurringId,
            invoiceNumber: this.invoiceNumber,
            status: this.status,
          };

          paymentDiag.componentInstance.userInfo = this.userInfo;
          paymentDiag.componentInstance.invoice = this.invoice;
          paymentDiag.componentInstance.invoiceSettings = this.invoiceSettings;
          paymentDiag.componentInstance.customer = this.customer;
          paymentDiag.componentInstance.customerBusinessContact = this.customerBusinessContact;
          paymentDiag.componentInstance.customerAddresses = this.customerAddresses;
          paymentDiag.componentInstance.customerSignature = this.customerSignature;
          paymentDiag.componentInstance.businessInfo = this.businessInfo;
          paymentDiag.componentInstance.locationData = this.locationData;
          paymentDiag.componentInstance.workOrder = this.workOrder;
          paymentDiag.componentInstance.displayBalance = this.recurring.amount;
          paymentDiag.componentInstance.hideManualPaymentOption =
            this.paymentConfig.hideManualPaymentOption;
          paymentDiag.componentInstance.showACHPaymentOption =
            this.paymentConfig.showACHPaymentOption;
          paymentDiag.componentInstance.showCashPayment =
            this.paymentConfig.showCashPayment;
          paymentDiag.componentInstance.showCreditCardOption =
            this.paymentConfig.showCreditCardOption;

          this.isProcessing = false;
          this.gridApi.refreshCells();
          this.onClose();
        }
      });
  }

  onCancelRecurringPayment(): void {
    this.isSaving = true;

    let dialog = this.modalService.open(RecurringInvoiceCancellationComponent, {
      size: 'md',
      keyboard: false,
      backdrop: 'static',
    });

    dialog.componentInstance.recurringInvoiceModel = {
      id: this.recurring.id,
      createdBy: this.userInfo.UserId,
      status: this.status,
    };
    dialog.componentInstance.invoiceModel = { invoiceNumber: this.invoiceNumber };
    dialog.componentInstance.status = 'Approved';
    dialog.componentInstance.displayApprovalNotes = true;

    dialog.result.then((res) => {
      if (res) {
        this.onClose(true);
      }
      this.isSaving = false;
    });
  }

  onPauseResumeRecurringPayment(action: string): void {
    this.isSaving = true;

    let dialog = this.modalService.open(PauseRecurringInvoiceDialogComponent, {
      size: 'sm',
      keyboard: false,
      backdrop: 'static',
    });

    dialog.result.then((res) => {
      if (res) {
        this.recurringInvoiceService[action]({
          recurringInvoiceId: this.recurring.id,
          userId: this.userInfo.UserId,
          resumeDate: res,
        }).subscribe((res) => {
          if (res) {
            this.alertService.alertSuccess({
              title: 'Success',
              text: 'Recurring payment successfully updated!',
            });
          } else {
            this.alertService.alertError({
              title: 'Error!',
              text: 'Something went wrong, please contact administrator.',
            });
          }
          this.onClose(true);
        });
      }
      this.isSaving = false;
    });
  }

  onSuspendRecurringPayment(): void {
    this.isSaving = true;

    let dialog = this.modalService.open(
      RecurringInvoiceSuspensionFormComponent,
      {
        size: 'sm',
        backdrop: 'static',
      }
    );

    dialog.componentInstance.recurringInvoiceId = this.recurring.id;
    dialog.componentInstance.source = 'recurring_invoice';

    dialog.result.then((res) => {
      if (res) {
        this.onClose(true);
      }
      this.isSaving = false;
    });
  }

  onViewSuspendDetails(): void {
    let dialog = this.modalService.open(
      RecurringInvoiceSuspensionFormComponent,
      {
        size: 'sm',
        backdrop: 'static',
      }
    );

    dialog.componentInstance.recurringInvoiceId = this.recurring.id;
    dialog.componentInstance.viewOnly = true;
    dialog.componentInstance.source = 'recurring_invoice';

    dialog.result.then((res) => {
      if (res) {
        this.onClose(true);
      }
    });
  }
}
